import Image from 'next/image'

/**
 * Product mug rendered from a real photograph rather than an SVG drawing.
 *
 * The photo is a plain white 11oz mug with a gold handle. The personalisation
 * is drawn as an SVG overlay sitting in the mug's print area, blended with
 * `multiply` so the artwork picks up the ceramic's highlights and shadow
 * instead of looking like a sticker floating above it.
 *
 * Coordinates below are in the photo's intrinsic 1600x1600 space, so the whole
 * overlay scales with whatever width the caller renders at.
 */

const PHOTO = { src: '/mug-gold.webp', w: 1600, h: 1600 }

// Print area on the visible face of the mug. The handle occupies the left
// third of the frame, so the printable centre sits right of the frame centre.
const PRINT = {
  cx: 1040,          // horizontal centre of the print area
  curveFrom: [640, 640] as const,
  curveCtrl: [1040, 500] as const,
  curveTo: [1440, 640] as const,
  nameBaseline: 1010,
  width: 800,        // usable width for the name, in viewBox units
}

export const MUG_NAME_MAX_FONT = 215
export const MUG_NAME_MIN_FONT = 90
export const MUG_PRINT_WIDTH = PRINT.width

export default function MugPhoto({
  uid,
  name = 'Emma',
  headline = 'HAPPY 30TH',
  nameFontSize = MUG_NAME_MAX_FONT,
  width = 240,
  priority = false,
  className,
}: {
  /** Unique per rendered instance — SVG ids must not collide on a page. */
  uid: string
  name?: string
  headline?: string
  nameFontSize?: number
  width?: number | string
  priority?: boolean
  className?: string
}) {
  const curveId = `mug-curve-${uid}`

  return (
    <div
      className={className}
      style={{ position: 'relative', width, maxWidth: '100%', lineHeight: 0 }}
    >
      <Image
        src={PHOTO.src}
        alt={`White ceramic mug printed with ${headline} ${name}`}
        width={PHOTO.w}
        height={PHOTO.h}
        priority={priority}
        sizes="(max-width: 768px) 60vw, 320px"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      />

      <svg
        viewBox={`0 0 ${PHOTO.w} ${PHOTO.h}`}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          mixBlendMode: 'multiply',
          pointerEvents: 'none',
        }}
      >
        <defs>
          <path
            id={curveId}
            fill="none"
            d={`M ${PRINT.curveFrom[0]} ${PRINT.curveFrom[1]} Q ${PRINT.curveCtrl[0]} ${PRINT.curveCtrl[1]} ${PRINT.curveTo[0]} ${PRINT.curveTo[1]}`}
          />
        </defs>

        {/* Slight barrel squeeze — the face of a cylinder falls away at the
            edges, so the artwork is scaled a touch narrower than flat. */}
        <g transform={`translate(${PRINT.cx} 0) scale(0.97 1) translate(${-PRINT.cx} 0)`}>
          <text
            fontFamily="'DM Sans', sans-serif"
            fontWeight="900"
            fontSize="118"
            fill="#E55934"
            letterSpacing="0.01em"
          >
            <textPath href={`#${curveId}`} startOffset="50%" textAnchor="middle">
              {headline}
            </textPath>
          </text>

          <text
            x={PRINT.cx}
            y={PRINT.nameBaseline}
            textAnchor="middle"
            fontFamily="'DM Sans', sans-serif"
            fontWeight="700"
            fontSize={nameFontSize}
            fill="#141414"
            letterSpacing="-0.03em"
            transform={`rotate(-1.6 ${PRINT.cx} ${PRINT.nameBaseline})`}
          >
            {name}
          </text>
        </g>
      </svg>
    </div>
  )
}

/**
 * Font size that lets `name` fit the mug's print width.
 *
 * Measured with canvas rather than estimated from character count — five W's
 * are ~60% wider than five average letters, so a per-character average
 * mis-shrinks wide-letter inputs.
 */
export function measureMugFontSize(name: string): number {
  if (typeof document === 'undefined') {
    // SSR estimate, refined on the client immediately after mount.
    const conservative = Math.floor(1100 / Math.max(name.length, 1))
    return Math.max(MUG_NAME_MIN_FONT, Math.min(MUG_NAME_MAX_FONT, conservative))
  }
  const ctx = document.createElement('canvas').getContext('2d')
  if (!ctx) return MUG_NAME_MAX_FONT
  ctx.font = `700 ${MUG_NAME_MAX_FONT}px 'DM Sans', sans-serif`
  const natural = ctx.measureText(name).width
  if (natural === 0 || natural <= MUG_PRINT_WIDTH) return MUG_NAME_MAX_FONT
  const scaled = Math.floor((MUG_NAME_MAX_FONT * MUG_PRINT_WIDTH) / natural)
  return Math.max(MUG_NAME_MIN_FONT, scaled)
}
